import React from 'react';
import { Link } from 'react-router-dom';
import { HiOutlineBriefcase, HiOutlineArrowRight } from 'react-icons/hi';
import { formatLongWeekdayMonthDayIST } from '../../utils/timezoneDisplay';

function LeaveDayBox({ title, dayLabel, count, staff, totalActive, accent }) {
  const accentStyles = {
    sky: {
      header: 'bg-sky-50/80 border-sky-100',
      badge: count > 0 ? 'bg-sky-600 text-white' : 'bg-gray-200 text-gray-600',
      type: 'text-sky-700',
    },
    violet: {
      header: 'bg-violet-50/80 border-violet-100',
      badge: count > 0 ? 'bg-violet-600 text-white' : 'bg-gray-200 text-gray-600',
      type: 'text-violet-700',
    },
  };
  const style = accentStyles[accent] || accentStyles.sky;

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white">
      <div className={`border-b px-3 py-2.5 ${style.header}`}>
        <div className="flex items-center justify-between gap-2">
          <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
          <span
            className={`inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-full px-2 text-xs font-bold ${style.badge}`}
          >
            {count}
          </span>
        </div>
        <p className="mt-0.5 text-[11px] text-gray-500 md:text-xs">{dayLabel}</p>
      </div>

      <div className="flex flex-1 flex-col px-3 py-3 md:px-4">
        {count === 0 ? (
          <p className="flex flex-1 items-center justify-center py-6 text-center text-sm text-gray-500">
            No one on leave
            {totalActive > 0 && accent === 'sky' ? ` · ${totalActive} active staff` : ''}
          </p>
        ) : (
          <>
            <p className="mb-2 text-xs text-gray-500">
              {count} staff member{count !== 1 ? 's' : ''}
            </p>
            <ul className="max-h-52 divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-100">
              {staff.map((member) => (
                <li
                  key={member.id}
                  className="flex flex-col gap-0.5 px-3 py-2 sm:flex-row sm:items-center sm:justify-between"
                >
                  <span className="text-sm font-medium text-gray-900">{member.full_name}</span>
                  <span className={`text-xs ${style.type}`}>
                    {member.leave_type
                      ? member.leave_type.charAt(0).toUpperCase() + member.leave_type.slice(1)
                      : 'Leave'}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}

const StaffOnLeaveTodayCard = ({ data, loading }) => {
  const today = data?.today;
  const tomorrow = data?.tomorrow;
  const totalActive = data?.totalActiveStaff ?? 0;

  const todayLabel = today?.date
    ? formatLongWeekdayMonthDayIST(new Date(`${today.date}T12:00:00+05:30`))
    : formatLongWeekdayMonthDayIST(new Date());
  const tomorrowLabel = tomorrow?.date
    ? formatLongWeekdayMonthDayIST(new Date(`${tomorrow.date}T12:00:00+05:30`))
    : '';

  return (
    <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="flex items-center justify-between gap-2 border-b border-gray-100 bg-gray-50/80 px-3 py-2 md:px-4 md:py-2.5">
        <div className="flex min-w-0 items-center gap-2">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-sky-100 md:h-10 md:w-10">
            <HiOutlineBriefcase className="h-5 w-5 text-sky-600" />
          </div>
          <h3 className="text-sm font-semibold text-gray-900 md:text-base">Staff on leave</h3>
        </div>
        <Link
          to="/staff"
          onClick={() => {
            try {
              sessionStorage.setItem('staffActiveTab', 'attendance');
            } catch {
              /* ignore */
            }
          }}
          className="shrink-0 text-xs font-medium text-primary-600 hover:text-primary-700 md:text-sm"
        >
          Attendance
          <HiOutlineArrowRight className="ml-0.5 inline h-3.5 w-3.5" />
        </Link>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-sky-400 border-t-transparent" />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 p-3 md:grid-cols-2 md:gap-4 md:p-4">
          <LeaveDayBox
            title="Today"
            dayLabel={todayLabel}
            count={today?.count ?? 0}
            staff={today?.staff || []}
            totalActive={totalActive}
            accent="sky"
          />
          <LeaveDayBox
            title="Tomorrow"
            dayLabel={tomorrowLabel}
            count={tomorrow?.count ?? 0}
            staff={tomorrow?.staff || []}
            totalActive={totalActive}
            accent="violet"
          />
        </div>
      )}
    </section>
  );
};

export default StaffOnLeaveTodayCard;
